import { useContext, useEffect, useRef, useState } from 'react'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { LabFeatureFlags } from 'constants/lab'
import MasterModalContext from 'contexts/MasterModalContext'
import ModalContext from 'contexts/ModalContext'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import {
  LOYALTY_CREDIT_CARD_EMPLOYEE_OFFER_PARAM,
  LOYALTY_CREDIT_CARD_EMPLOYEE_OFFER_VALUE,
} from 'loyalty/pages/loyaltyCreditCardPage/loyaltyCreditCardConstants'
import { selectLoggedIn } from 'selectors/accountSelectors'

// How long to hold the page after a forced login before giving up on a fresh
// LE Lab decision. A failed decide call returns no data and merges nothing
// into state, so without a deadline the page would wait for a decision that
// is never coming. The decide call itself times out after a second, so a
// decision that has not arrived by now is not on its way; waiting longer only
// adds to the spinner before the visitor lands on the Société page, which is
// where a visitor we cannot place ends up regardless.
export const POST_LOGIN_DECISION_TIMEOUT_MS = 2000

/**
 * Opens a login modal that cannot be dismissed when the credit card page
 * cannot decide whether a logged-out visitor may see it. Staff arriving on
 * their shared link get employee-specific copy; anyone else gets the standard
 * login copy.
 *
 * Returns whether the page is still waiting on a decision, so it can hold its
 * loading state rather than acting on the anonymous one. LE Lab decided this
 * page before the visitor was identified; logging in makes the app decide
 * again (`refreshLabFeatureFlagsForCurrentUser`, dispatched once the seed id
 * has been stitched), and the page renders or redirects on whatever that
 * returns.
 *
 * The wait has to be known during render, not from an effect: the redirect is
 * a child component, and React runs child effects before the parent's, so a
 * redirect that renders at all navigates away before this hook could react.
 */
export function useLoyaltyCreditCardForcedLogin(requiresLogin: boolean): boolean {
  const flag = useLabFeatureFlag(LabFeatureFlags.societeCreditCardFlag)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const queryParams = useQueryParams()
  const hasForcedLoginRef = useRef(false)
  const flagWhenLoginForcedRef = useRef<App.LabFeatureFlagResult | undefined>(undefined)
  const [hasWaitedForDecision, setHasWaitedForDecision] = useState(false)
  const isEmployeeOfferVisit = queryParams.get(LOYALTY_CREDIT_CARD_EMPLOYEE_OFFER_PARAM) === LOYALTY_CREDIT_CARD_EMPLOYEE_OFFER_VALUE

  // A refreshed decision arrives as a new object, so anything still identical
  // to what we forced the login on means the re-decide has not landed yet.
  const hasDecidedSinceLogin = flag !== flagWhenLoginForcedRef.current
  const isAwaitingDecision = hasForcedLoginRef.current && isLoggedIn && !hasDecidedSinceLogin && !hasWaitedForDecision

  useEffect(() => {
    if (!requiresLogin || modalState.open || hasForcedLoginRef.current) {
      return
    }
    hasForcedLoginRef.current = true
    flagWhenLoginForcedRef.current = flag
    showModal(
      <AccountAccessModal
        initialMode={isEmployeeOfferVisit ? 'employeeOfferLogin' : 'login'}
        dismissable={false}
      />,
    )
  }, [requiresLogin, modalState.open, showModal, isEmployeeOfferVisit, flag])

  useEffect(() => {
    if (!isAwaitingDecision) {
      return
    }
    const timeoutId = window.setTimeout(() => setHasWaitedForDecision(true), POST_LOGIN_DECISION_TIMEOUT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isAwaitingDecision])

  return isAwaitingDecision
}
